import './Table.css'
import { cn } from '../lib/utils'
import {
   useReactTable,
   getCoreRowModel,
   flexRender,
   getPaginationRowModel,
   getSortedRowModel,
} from '@tanstack/react-table'
import Pagination from './components/Pagination'
import { useState } from 'react'
import TableResume from './components/TableResume'
import { Button } from '../Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShareFromSquare } from '@fortawesome/free-regular-svg-icons'
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'

type PropTypes = {
   data: Array<any>
   columns: Array<any>
   pagination?: {
      totalSize: number
      defaultPageSize: number
      defaultPageIndex: number
      onPageChange: (page: { page: number; size: number }) => void
   }
   showTableResume?: boolean
   fetching?: boolean
   containerClassName?: string
   tableContainerClassName?: string
   tableClassName?: string
   footerClassName?: string
   onExport?: () => void
   exportText?: string
   children?: any
   sorting?: {
      sorting: any
      onSortChange: (sorting: any) => void
   }
   customBorderRadiusSupport?: boolean
}

export const Table = ({
   data,
   columns,
   pagination,
   showTableResume,
   fetching,
   containerClassName,
   tableContainerClassName,
   footerClassName,
   tableClassName,
   children,
   sorting,
   customBorderRadiusSupport,
}: PropTypes) => {
   const [pageIndex, setPageIndex] = useState(pagination?.defaultPageIndex || 0)
   const { getHeaderGroups, getRowModel, getPageCount, getAllLeafColumns } = useReactTable({
      columns,
      data,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      manualPagination: true,
      defaultColumn: {
         size: 51,
         minSize: 50,
      },
      ...(sorting && {
         // manualSorting solo con paginación: ahí el backend ordena; sin paginación ordena la tabla
         manualSorting: !!pagination,
         onSortingChange: sorting.onSortChange,
      }),
      state: {
         ...(sorting && {
            sorting: sorting.sorting,
         }),
         ...(pagination && {
            pagination: {
               pageIndex: pageIndex,
               pageSize: pagination.defaultPageSize,
            },
         }),
      },
      ...(pagination && {
         getPaginationRowModel: getPaginationRowModel(),
         pageCount: Math.ceil(pagination.totalSize / pagination.defaultPageSize),
      }),
   })

   const gotoPage = (newPage: number) => {
      setPageIndex(newPage)
      if (pagination) {
         pagination.onPageChange({
            page: newPage,
            size: pagination.defaultPageSize,
         })
      }
   }
   return (
      <div className={cn('flex flex-col', containerClassName)}>
         <div
            className={cn('relative overscroll-x-auto overflow-x-auto', tableContainerClassName, {
               'itm-table-container border border-gray-200': customBorderRadiusSupport,
               'mb-4': pagination || showTableResume,
            })}
         >
            {(showTableResume && pagination) || children ? (
               <div className="flex justify-between mb-4 px-4">
                  <div className="flex items-end">
                     {showTableResume && pagination && (
                        <TableResume page={pageIndex} size={pagination.defaultPageSize} total={pagination.totalSize} />
                     )}
                  </div>
                  {/* {onExport && exportText && (
            <Button onClick={onExport} className={"flex gap-x-2 items-center text-[12px]"}>
              <FontAwesomeIcon icon={faShareFromSquare} className={'text-gray-500 text '}/>
              {exportText}
            </Button>
        )} */}
                  {children && children}
               </div>
            ) : null}
            {fetching && <div className="absolute inset-0 bg-gray-200 bg-opacity-50" />}
            <table
               className={cn('w-full max-w-full divide-y divide-gray-200 table-fixed', tableClassName, {
                  'itm-table': customBorderRadiusSupport,
                  'border border-gray-200': !customBorderRadiusSupport,
               })}
            >
               <thead className="bg-gray-50 align-top">
                  {getHeaderGroups().map((headerGroup, headerGroupIndex) => (
                     <tr key={headerGroup.id}>
                        {headerGroup.headers
                           // tanstack repeats every leaf header once per header row (as a placeholder);
                           // only render it in its own depth's row, spanning the remaining rows below.
                           .filter((header) => header.column.depth === headerGroup.depth)
                           .map((header) => {
                              const isGroup = header.column.columns.length > 0
                              const isCentered = isGroup || !!header.column.parent
                              const rowSpan = isGroup ? 1 : getHeaderGroups().length - headerGroupIndex
                              return (
                                 <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    rowSpan={rowSpan}
                                    {...(!isGroup &&
                                       header.getSize() !== 51 && { style: { width: `${header.getSize()}px` } })}
                                    className={cn(
                                       'px-4 py-3 text-sm font-medium text-gray-500 whitespace-nowrap overflow-hidden overflow-ellipsis relative',
                                       isCentered ? 'text-center' : 'text-left',
                                    )}
                                    {...(!isGroup &&
                                       header.column.getCanSort() && {
                                          onClick: header.column.getToggleSortingHandler(),
                                       })}
                                 >
                                    <div className={cn('flex items-center gap-1', isCentered && 'justify-center')}>
                                       {flexRender(header.column.columnDef.header, header.getContext())}
                                       {!isGroup &&
                                          (header.column.getIsSorted() === 'asc' ? (
                                             <FontAwesomeIcon icon={faSortUp} className="text-gray-500 text-xs" />
                                          ) : header.column.getIsSorted() === 'desc' ? (
                                             <FontAwesomeIcon icon={faSortDown} className="text-gray-500 text-xs" />
                                          ) : (
                                             header.column.getCanSort() && (
                                                <FontAwesomeIcon icon={faSort} className="text-gray-400 text-xs" />
                                             )
                                          ))}
                                    </div>
                                 </th>
                              )
                           })}
                     </tr>
                  ))}
               </thead>
               <tbody className="bg-white divide-y divide-gray-200 align-top">
                  {getRowModel().rows.length === 0 && fetching && (
                     <tr>
                        <td colSpan={getAllLeafColumns().length} className="text-center px-4 py-2">
                           Cargando ...
                        </td>
                     </tr>
                  )}
                  {getRowModel().rows.length === 0 && !fetching && (
                     <tr>
                        <td colSpan={getAllLeafColumns().length} className="text-center px-4 py-2">
                           No se encontraron elementos
                        </td>
                     </tr>
                  )}
                  {getRowModel().rows.map((row, i) => {
                     return (
                        <tr key={row.id} className={`${i % 2 !== 0 ? 'bg-gray-50' : ''} hover:bg-gray-100`}>
                           {row.getVisibleCells().map((cell) => (
                              <td
                                 key={cell.id}
                                 className={`border-none px-4 py-2 whitespace-nowrap ${
                                    !(cell.column as any)?.overflowVisible
                                       ? 'overflow-hidden overflow-ellipsis hover:whitespace-normal'
                                       : ''
                                 }`}
                              >
                                 {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                           ))}
                        </tr>
                     )
                     // prepareRow(row)
                     // return (
                     //    <tr {...row.getRowProps()} className={`${i % 2 !== 0 ? 'bg-gray-50' : ''} hover:bg-gray-100`}>
                     //       {row.cells.map((cell) => (
                     //          <td
                     //             {...cell.getCellProps()}
                     //             className={`border-none px-4 py-2 whitespace-nowrap ${
                     //                !(cell.column as any)?.overflowVisible
                     //                   ? 'overflow-hidden overflow-ellipsis hover:whitespace-normal'
                     //                   : ''
                     //             }`}
                     //          >
                     //             {cell.render('Cell')}
                     //          </td>
                     //       ))}
                     //    </tr>
                     // )
                  })}
               </tbody>
            </table>
         </div>
         {pagination && (
            <div className={cn('flex justify-between mb-4', footerClassName)}>
               <div>
                  {showTableResume && (
                     <TableResume page={pageIndex} size={pagination.defaultPageSize} total={pagination.totalSize} />
                  )}
               </div>
               <Pagination
                  pageIndex={pageIndex}
                  nextPage={() => gotoPage(pageIndex + 1)}
                  previousPage={() => gotoPage(pageIndex - 1)}
                  gotoPage={gotoPage}
                  pageCount={getPageCount()}
               />
            </div>
         )}
      </div>
   )
}
